import { StateCreator } from 'zustand';
import { AppState } from '../types';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// TYPE DEFINITIONS
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export interface BettyMessage {
    role: 'user' | 'betty';
    text: string;
}

export interface BettySliceType {
    isBettyOpen: boolean;
    isBettyLoading: boolean;
    bettyMessages: BettyMessage[];
    dictatedCoachNote: string;
    toggleBetty: () => void;
    closeBetty: () => void;
    clearBettyChat: () => void;
    setDictatedCoachNote: (text: string) => void;
    sendBettyMessage: (text: string, route: string) => Promise<void>;
}

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// SLICE CREATOR 
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

export const createBettySlice: StateCreator<AppState, [], [], BettySliceType> = (set, get) => ({
    isBettyOpen: false,
    isBettyLoading: false,
    bettyMessages: [
        { role: 'betty', text: 'Hallo! Ich bin Betty, deine Assistenz. Wie kann ich dir helfen?' }
    ],
    
    dictatedCoachNote: '',

    toggleBetty: () => set((state) => ({ isBettyOpen: !state.isBettyOpen })),
    
    closeBetty: () => set({ isBettyOpen: false }),

    clearBettyChat: () => set({ 
        bettyMessages: [
            { role: 'betty', text: 'Hallo! Ich bin Betty, deine Assistenz. Wie kann ich dir helfen?' }
        ] 
    }),

    setDictatedCoachNote: (text: string) => set({ dictatedCoachNote: text }),

    sendBettyMessage: async (text: string, route: string) => {
        const state = get();

        // INTERCEPTOR-WEICHE (GHOSTWRITER)
        const normalizedRoute = route.toLowerCase();
        if (normalizedRoute === 'coach-akte' || normalizedRoute === 'coach_akte' || route === 'Coach-Akte') {
            set({ dictatedCoachNote: text });
            return; // ABBRUCH: KI wird nicht kontaktiert, Nachricht geht nur in die Akte
        }
        
        // 1. User-Nachricht ins UI setzen & Lade-Animation starten
        set({
            bettyMessages: [...state.bettyMessages, { role: 'user', text }],
            isBettyLoading: true
        });

        try {
            // Europa-Routing
            const app = getApp();
            const functions = getFunctions(app, 'europe-west1');
            const askBetty = httpsCallable(functions, 'askBettyAppAi');

            // Namen des Mandanten auslesen (für Radar + Anrede)
            const firstName = state.clientData?.vorname;
            const lastName = state.clientData?.nachname;
            const salonName = state.clientData?.firmenname;
            const person = [firstName, lastName].filter(Boolean).join(' ').trim();
            const userName = person || salonName || 'Mandant';
            const radarSalon = person && salonName && person !== salonName
                ? `${person} (${salonName})`
                : (person || salonName || userName);

            // Dynamische Kontext-Injektion (Das Master-Regelwerk liegt jetzt im Backend!)
            const dynamicDirectives = [
                `WICHTIGE SYSTEM-INFO: Der Nutzer, mit dem du gerade sprichst, heißt "${userName}". Sprich ihn gelegentlich natürlich, freundlich und wertschätzend mit seinem Namen an.`
            ];

            // 2. Call an das Backend senden
            const response = await askBetty({
                message: text,
                route: route,
                systemDirectives: dynamicDirectives, // Übermittelt nur noch den Namen
                chatHistory: state.bettyMessages.slice(-6),
                salonName: radarSalon,
                mandantName: radarSalon,
            });

            const data = response.data as { success: boolean; text: string };
            
            // 3. Antwort ins UI pushen & Laden beenden
            set((currentState) => ({
                bettyMessages: [...currentState.bettyMessages, { role: 'betty', text: data.text }],
                isBettyLoading: false
            }));

        } catch (error: any) {
            console.error("Betty API Error:", error);
            
            // 4. Bei Fehler saubere Meldung ausgeben
            set((currentState) => ({
                bettyMessages: [...currentState.bettyMessages, { 
                    role: 'betty', 
                    text: `⚠️ Systemfehler: ${error.message || 'Die Verbindung zur KI ist fehlgeschlagen. Bitte versuche es später noch einmal.'}` 
                }],
                isBettyLoading: false
            }));
        }
    }
});